// src/services/api/index.js
// Service API yang handle dua mode: Development & Production

const IS_DEV = process.env.NODE_ENV === 'development';

// ════════════════════════════════════════════════════════════
// SYSTEM PROMPT (sama dengan functions/api/analyze.js)
// ════════════════════════════════════════════════════════════
const SYSTEM_PROMPT = `Anda adalah pakar Nahwu & Sharaf versi I'rab Kitab/Pesantren.
Tugas Anda: Analisislah teks Arab berikut per-bagian/frasa secara ringkas dan hemat token.

Gunakan istilah I'rab standar Arab (misal: فعل ماضٍ, فاعل, حرف جر, اسم مجرور, مضاف إليه, مفعول به, معطوف, dll).

Untuk SETIAP kata, berikan 3 field:
- "p" : kata Arab dengan harakat (contoh: وَصَلَتْ)
- "r" : peran i'rab (contoh: فعل ماضٍ مبني على الفتح)
- "m" : arti kata dalam bahasa Indonesia, SINGKAT 1-3 kata (contoh: "telah sampai", "makanan", "huruf athaf")

Keluaran HARUS JSON MURNI:
{
  "note": "catatan singkat jika ada (opsional)",
  "chunks": [
    {
      "title": "Bagian 1",
      "arabic": "kalimat berharakat",
      "translation": "terjemahan frasa",
      "irab": [
        {"p": "kata", "r": "i'rab", "m": "arti"}
      ]
    }
  ]
}`;

// ════════════════════════════════════════════════════════════
// FUNGSI UTAMA: analyzeText
// Dipakai oleh useAnalysis.js
// ════════════════════════════════════════════════════════════
export async function analyzeText(text, provider = 'gemini') {
  if (IS_DEV) {
    return analyzeTextDev(text, provider);
  }
  return analyzeTextProd(text, provider);
}

// ── Production: Lewat Cloudflare Function ───────────────────
async function analyzeTextProd(text, provider) {
  const res = await fetch('/api/analyze', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ text: text.trim(), provider }),
  });

  const data = await res.json().catch(() => ({
    error: 'Gagal membaca respons dari server.',
  }));

  if (!res.ok) {
    throw new Error(data.error || `Server error (${res.status})`);
  }

  return data;
}

// ── Development: Panggil AI API langsung ───────────────────
async function analyzeTextDev(text, provider) {
  if (provider === 'cohere') {
    return callCohereDev(text);
  }
  return callGeminiDev(text);
}

// ── Dev: Call Gemini Langsung ─────────────────────────────
async function callGeminiDev(text) {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

  if (!apiKey) {
    throw new Error(
      '[DEV] Gemini API Key tidak ditemukan.\n' +
      'Buat file .env.local dan isi:\n' +
      'REACT_APP_GEMINI_API_KEY=AIzaSy...'
    );
  }

  const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`;

  const res = await fetch(url, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [{
        parts: [{
          text: `Analisislah teks Arab ini. Setiap kata WAJIB menyertakan field "m" (arti singkat): "${text}"`
        }]
      }],
      systemInstruction: {
        parts: [{ text: SYSTEM_PROMPT }]
      },
      generationConfig: {
        responseMimeType: 'application/json',
        temperature: 0.1,
      },
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Gemini error (${res.status})`);
  }

  const data    = await res.json();
  const rawText = data.candidates?.[0]?.content?.parts?.[0]?.text;

  if (!rawText) throw new Error('Gemini tidak mengembalikan hasil.');

  return parseJsonSafely(rawText);
}

// ── Dev: Call Cohere Langsung ─────────────────────────────
async function callCohereDev(text) {
  const apiKey = process.env.REACT_APP_COHERE_API_KEY;
  const model  = process.env.REACT_APP_COHERE_MODEL || 'command-r-08-2024';

  if (!apiKey) {
    throw new Error(
      '[DEV] Cohere API Key tidak ditemukan.\n' +
      'Buat file .env.local dan isi:\n' +
      'REACT_APP_COHERE_API_KEY=your-key...'
    );
  }

  const res = await fetch('https://api.cohere.com/v1/chat', {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model,
      message: `Analisislah teks ini. Setiap kata WAJIB menyertakan field "m" (arti singkat): "${text}"`,
      preamble: SYSTEM_PROMPT,
      response_format: { type: 'json_object' },
      temperature: 0.1,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `Cohere error (${res.status})`);
  }

  const data    = await res.json();
  const rawText = data.text;

  if (!rawText) throw new Error('Cohere tidak mengembalikan hasil.');

  return parseJsonSafely(rawText);
}

// ── Helper: Parse JSON Safely ─────────────────────────────
function parseJsonSafely(raw) {
  let cleaned = raw.trim();

  if (cleaned.startsWith('```')) {
    cleaned = cleaned
      .replace(/^```(?:json)?\s*/i, '')
      .replace(/\s*```$/i, '');
  }

  const first = cleaned.indexOf('{');
  const last  = cleaned.lastIndexOf('}');

  if (first !== -1 && last !== -1 && last > first) {
    cleaned = cleaned.substring(first, last + 1);
  }

  return JSON.parse(cleaned);
}

// ════════════════════════════════════════════════════════════
// EXPORTS
// ════════════════════════════════════════════════════════════
export { IS_DEV };
export default analyzeText;